import { createClient } from '@connectrpc/connect'
import { createConnectTransport } from '@connectrpc/connect-web'
import { AiChatService } from '../../../../../proto/_gen/aiChat_connect'
import { SubscribeAiChatEventByIdRequest } from '../../../../../proto/_gen/aiChat_pb'
import { AiChatEvent_EventType, AiChatEvent_PayloadJsonStatus, AiChatEvent as ProtoAiChatEvent } from '../../../../../proto/_gen/_gen.models_pb'
import { authenticator } from '../../../../pages/_shared/authService'
import type { AiChatEvent, AiChatEventPayload } from 'poc-ai-biz-irdb_domain-model'
import { assertNever } from '../../../../../_shared/lib'
import { changeKeysToCamelCaseRecursively } from '../../../../../_shared/case'

declare var IRDB_BACKEND_API_URL: string | undefined

// Protobuf AiChatEvent を entities.ts AiChatEvent に変換する関数
function convertProtoAiChatEventToEntityAiChatEvent(protoEvent: ProtoAiChatEvent): AiChatEvent {
  // payloadのStruct型から具体的な型への変換
  let payload: AiChatEventPayload

  try {
    // Protobuf StructからJSONを取得し、型付きpayloadに変換
    const payloadJson = protoEvent.payload?.toJson() as any

    // eventTypeに基づいてpayloadの型を決定
    switch (protoEvent.eventType) {
      case AiChatEvent_EventType.USER_MESSAGE:
        payload = {
          type: 'userMessage',
          content: payloadJson?.content || '',
        }
        break
      case AiChatEvent_EventType.ASSISTANT_MESSAGE:
        payload = {
          type: 'assistantMessage',
          content: payloadJson?.content || '',
          pause: payloadJson?.pause || false,
        }
        break
      case AiChatEvent_EventType.SERVER_SIDE_TOOL_CALL:
        payload = {
          type: 'serverSideToolCall',
          toolName: payloadJson?.toolName || '',
          displayTextForUser: payloadJson?.displayTextForUser || '',
          status: payloadJson?.status || 'executing',
          parameters: payloadJson?.parameters || {},
          result: payloadJson?.result || null,
        }
        break
      case AiChatEvent_EventType.CLIENT_INFO:
        payload = {
          type: 'clientInfo',
          pageContext: payloadJson?.pageContext || { path: '', pageContent: {} },
          availableActions: payloadJson?.availableActions || [],
        }
        break
      case AiChatEvent_EventType.CLIENT_ACTION:
        payload = {
          type: 'clientAction',
          actionType: payloadJson?.actionType || '',
          displayTextForUser: payloadJson?.displayTextForUser || '',
          parameters: payloadJson?.parameters || {},
        }
        break
      case AiChatEvent_EventType.ERROR:
        payload = {
          type: 'error',
          errorType: payloadJson?.errorType || '',
          message: payloadJson?.message || '',
          details: payloadJson?.details,
        }
        break
      default:
        // フォールバック: エラーとして扱う
        payload = {
          type: 'error',
          errorType: 'UNKNOWN_EVENT_TYPE',
          message: `Unknown event type: ${protoEvent.eventType}`,
        }
    }
  } catch (error) {
    // payloadの変換に失敗した場合のフォールバック
    payload = {
      type: 'error',
      errorType: 'PAYLOAD_CONVERSION_ERROR',
      message: `Failed to convert payload: ${error instanceof Error ? error.message : 'Unknown error'}`,
    }
  }

  return {
    id: protoEvent.id,
    aiChatSessionId: protoEvent.aiChatSessionId,
    userId: protoEvent.userId,
    eventIndex: protoEvent.eventIndex,
    eventType: payload.type,
    payload,
    payloadJsonStatus: (() => {
      switch (protoEvent.payloadJsonStatus) {
        case AiChatEvent_PayloadJsonStatus.GENERATING:
          return 'generating'
        case AiChatEvent_PayloadJsonStatus.COMPLETED:
          return 'completed'
        default:
          throw new Error(`Unknown payloadJsonStatus: ${protoEvent.payloadJsonStatus}`)
      }
    })(),
    createdAt: new Date(protoEvent.createdAt),
  }
}

export const useSubscribeAiChatEventById = () => {
  const backendApiUrl = IRDB_BACKEND_API_URL
  if (!backendApiUrl) {
    throw new Error('The IRDB_BACKEND_API_URL environment variable is not set')
  }

  const transport = createConnectTransport({
    baseUrl: backendApiUrl,
    interceptors: [authenticator],
  })
  const client = createClient(AiChatService, transport)

  return async function* (request: { aiChatEventId: string }) {
    // domain-model型をProtobuf型に変換
    const protoRequest = new SubscribeAiChatEventByIdRequest({
      aiChatEventId: request.aiChatEventId,
    })

    const stream = client.subscribeAiChatEventById(protoRequest)

    for await (const response of stream) {
      if (response.error) {
        throw new Error(response.error.message || 'Unknown error occurred during event subscription')
      }

      if (response.aiChatEvent) {
        // Protobuf AiChatEvent を entities.ts AiChatEvent に変換
        const convertedEvent = convertProtoAiChatEventToEntityAiChatEvent(response.aiChatEvent)
        yield convertedEvent
      }
    }
  }
}
